import { captureException } from '@sentry/react';
import type { Dispatch, RefObject, SetStateAction } from 'react';
import { useEffect, useRef, useState } from 'react';

import { useLatest } from '../../../utils/use-latest';
import { loadFaceDetector } from './faceDetector';
import type { Framing } from './faceFraming';
import { evaluateFraming, stabilise } from './faceFraming';

/** Inference cadence. The overlay still redraws every frame from the last result. */
const DETECT_INTERVAL_MS = 100;
/**
 * Degraded mode: no overlay, no gate, shutter always live. Used when the
 * detector fails to load AND when a loaded detector throws mid-session —
 * "detection is an assist, it never holds the shutter hostage" (spec §4).
 */
const FREE_FRAMING: Framing = { ok: true, hint: null, crop: null };

interface FaceDetectionOptions {
  videoRef: RefObject<HTMLVideoElement | null>;
  /** A playing stream: the loop only runs, and is only torn down, on its edges. */
  ready: boolean;
  /** Owned by the camera stream, which resets it per session. */
  framing: Framing;
  setFraming: Dispatch<SetStateAction<Framing>>;
}

/**
 * Detection loop, throttled: feeds `framing` from the live preview while the
 * stream is ready. Returns whether detection is down for this session
 * (degraded mode).
 */
export function useFaceDetection({
  videoRef,
  ready,
  framing,
  setFraming,
}: FaceDetectionOptions): boolean {
  const rafRef = useRef<number | null>(null);
  const lastDetectRef = useRef(0);
  /**
   * The frame is copied here and the COPY is what the detector sees. Handing
   * MediaPipe the <video> element directly returns a detection that is offset
   * and over-scaled: the bounding box and the keypoints agree with each other
   * and all of them disagree with the picture, so every number downstream
   * looks self-consistent while the square sits beside the subject. A canvas
   * snapshot of the very same frame detects correctly. One drawImage per
   * detection, ten times a second — do not "optimise" it away.
   */
  const snapshotRef = useRef<HTMLCanvasElement | null>(null);

  const [detectionDown, setDetectionDown] = useState(false);

  // The detection loop's closure is built once per session, so it cannot read
  // `framing` — but the easing and the hysteresis both need last frame's
  // verdict. Same ref mirror as the camera stream's `errorRef`.
  const framingRef = useLatest(framing);

  // Every setter below an await is gated on `session.signal.aborted` (or on
  // a predicate that reads it). react-doctor only recognises a `let` flag
  // flipped by the cleanup, which no-unnecessary-condition rejects because
  // narrowing cannot see the cleanup.
  // react-doctor-disable-next-line react-doctor/no-set-state-after-await-in-effect
  useEffect(() => {
    if (!ready) {
      return;
    }
    // Detection health is re-derived per session, never inherited. The two
    // places that latch it below are not the same kind of failure: a detector
    // that fails to LOAD is gone for the session — `loadFaceDetector` memoises
    // the null result in a module-level promise — whereas a loaded detector
    // that THROWS mid-session (WebGL context loss, GPU reset) leaves the
    // singleton perfectly usable, and the next stream gives real verdicts
    // again. Carrying that transient latch over hid the overlay and pinned the
    // hint to "detection unavailable" while `framing` was live and still
    // gating the shutter — a dead button next to a message about the wrong
    // problem. A genuine load failure re-latches from the memoised promise
    // almost immediately, so degraded mode is not lost, only recomputed.
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- re-deriving this IS the effect's job: each detection session must decide for itself whether detection works
    setDetectionDown(false);
    // Aborted by this effect's cleanup; a plain `let` flag reads as always
    // false to control-flow narrowing, which cannot see the cleanup.
    const session = new AbortController();

    void (async () => {
      const detector = await loadFaceDetector();
      if (session.signal.aborted) {
        return;
      }
      if (!detector) {
        setDetectionDown(true);
        setFraming(FREE_FRAMING);
        return;
      }

      const tick = () => {
        if (session.signal.aborted) {
          return;
        }
        rafRef.current = requestAnimationFrame(tick);

        const video = videoRef.current;
        if (!video || video.readyState < 2) {
          return;
        }

        const now = performance.now();
        if (now - lastDetectRef.current < DETECT_INTERVAL_MS) {
          return;
        }
        lastDetectRef.current = now;

        try {
          const snapshot = (snapshotRef.current ??=
            document.createElement('canvas'));
          if (
            snapshot.width !== video.videoWidth ||
            snapshot.height !== video.videoHeight
          ) {
            snapshot.width = video.videoWidth;
            snapshot.height = video.videoHeight;
          }
          const snapshotCtx = snapshot.getContext('2d');
          // No context means no copy, and detection on a blank canvas reports
          // "no face" — which would lock the shutter behind a hint about a
          // face that is sitting right there. Throw instead: the catch below
          // degrades detection and frees the shutter, which is the rule this
          // component holds to everywhere else.
          if (!snapshotCtx) {
            throw new Error('no 2D context for the detection snapshot');
          }
          snapshotCtx.drawImage(video, 0, 0);

          const previous = framingRef.current;
          setFraming(
            stabilise(
              previous,
              evaluateFraming(
                detector.detect(snapshot, now),
                video.videoWidth,
                video.videoHeight,
                previous.ok,
              ),
            ),
          );
        } catch (err) {
          // A loaded detector can still throw: GPU/WebGL teardown, a
          // mismatched wasm/model pair, an unexpected frame state. The throw
          // escapes the rAF callback but the loop it already scheduled
          // survives, so left alone this rethrows ten times a second forever
          // behind a live preview and a shutter that never unlocks. Stop the
          // loop, degrade exactly as a failed load does, and report once —
          // per frame would be a Sentry flood.
          // Set at the top of this tick, so a frame is always scheduled here.
          cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
          session.abort();
          captureException(err);
          setDetectionDown(true);
          setFraming(FREE_FRAMING);
        }
      };
      tick();
    })();

    return () => {
      session.abort();
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = null;
    };
    // framingRef is stable: useLatest returns the same ref every render.
  }, [ready, videoRef, setFraming, framingRef]);

  return detectionDown;
}
